import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Button, Icon, Loader, Checkbox, MenuInput, AlertBanner } from '@saviynt/design-system';
import { useDispatch } from 'react-redux';
import { saveUserAttributeMapping } from '../../redux/migrationThunks';
import { ATTRIBUTE_MAPPING_LABELS } from '../../constants/stringConstants';
import useStyles from './styles/AttributeMappingContent.styles';

function AttributeMappingContent({
  sourceID,
  sourceType,
  userAttributes,
  userAttributesLoading,
  saveMappingLoading,
  saveMappingError,
  onSave,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [saviyntAttribute, setSaviyntAttribute] = useState('');
  const [sourceUserAttribute, setSourceUserAttribute] = useState('');
  const [saviyntOpts, setSaviyntOpts] = useState([]);
  const [sourceOpts, setSourceOpts] = useState([]);
  const [mappedAttributes, setMappedAttributes] = useState([]);

  const [selectedMappings, setSelectedMappings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ visible: false, type: 'Success', message: '' });

  const isOIM = sourceType?.toLowerCase().includes('oim');
  const sourceAttributeLabel = isOIM
    ? ATTRIBUTE_MAPPING_LABELS.OIM_ATTRIBUTE
    : ATTRIBUTE_MAPPING_LABELS.SAILPOINT_ATTRIBUTE;
  const sourceAttributePlaceholder = isOIM
    ? ATTRIBUTE_MAPPING_LABELS.SELECT_OIM
    : ATTRIBUTE_MAPPING_LABELS.SELECT_SAILPOINT;

  const errorSelectBoth = isOIM
    ? ATTRIBUTE_MAPPING_LABELS.ERROR_SELECT_BOTH_OIM
    : ATTRIBUTE_MAPPING_LABELS.ERROR_SELECT_BOTH;

  const saviyntAttributes = userAttributes?.saviyntUserAttributes || [];
  const sourceUserAttributes = userAttributes?.sourceUserAttributes || [];
  const availableSaviyntAttributes = saviyntAttributes.filter(
    (attr) => !mappedAttributes.some((mapping) => mapping.saviyntProperty === attr)
  );

  useEffect(() => {
    setSaviyntOpts(
      availableSaviyntAttributes.map((a) => ({ label: a, value: a, isChecked: false }))
    );
  }, [userAttributes, mappedAttributes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSourceOpts(sourceUserAttributes.map((a) => ({ label: a, value: a, isChecked: false })));
  }, [userAttributes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (saveMappingError) {
      setBanner({ visible: true, type: 'Critical', message: saveMappingError });
    }
  }, [saveMappingError]);

  useEffect(() => {
    if (userAttributes?.presentMapping) {
      const presentMapping = userAttributes.presentMapping || [];
      setMappedAttributes(presentMapping.map((m) => ({ ...m, id: crypto.randomUUID() })));
    }
  }, [userAttributes]);

  const handleAddMapping = () => {
    if (!saviyntAttribute || !sourceUserAttribute) {
      setBanner({ visible: true, type: 'Critical', message: errorSelectBoth });
      return;
    }
    setMappedAttributes((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        saviyntProperty: saviyntAttribute,
        sourceProperty: sourceUserAttribute,
      },
    ]);
    setSaviyntAttribute('');
    setSourceUserAttribute('');
    setSaviyntOpts((prev) => prev.map((o) => ({ ...o, isChecked: false })));
    setSourceOpts((prev) => prev.map((o) => ({ ...o, isChecked: false })));
    setBanner((prev) => ({ ...prev, visible: false }));
  };

  const handleToggleSelect = (id) => {
    setSelectedMappings((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedMappings(
      selectedMappings.length === mappedAttributes.length ? [] : mappedAttributes.map((m) => m.id)
    );
  };

  const handleDeleteSelected = () => {
    setMappedAttributes((prev) => prev.filter((m) => !selectedMappings.includes(m.id)));
    setSelectedMappings([]);
    setBanner((prev) => ({ ...prev, visible: false }));
  };

  const handleSave = async () => {
    if (mappedAttributes.length === 0) {
      setBanner({
        visible: true,
        type: 'Critical',
        message: ATTRIBUTE_MAPPING_LABELS.ERROR_MIN_ONE_MAPPING,
      });
      return;
    }
    const payload = mappedAttributes.map(({ saviyntProperty, sourceProperty }) => ({
      saviyntProperty,
      sourceProperty,
    }));
    setSaving(true);
    try {
      const result = await dispatch(
        saveUserAttributeMapping({ sourceId: sourceID, userAttributeMapping: payload })
      ).unwrap();
      if (onSave) onSave(mappedAttributes, result.message);
      setBanner({
        visible: true,
        type: 'Success',
        message: result.message || 'Attribute mappings saved successfully.',
      });
    } catch (err) {
      setBanner({
        visible: true,
        type: 'Critical',
        message: err?.message || saveMappingError || 'Failed to save attribute mappings.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (userAttributesLoading && !userAttributes) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  return (
    <>
      {saving && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      {banner.visible && (
        <AlertBanner
          colorTheme={banner.type}
          title={banner.type === 'Success' ? 'Success' : 'Error'}
          description={banner.message}
          isVisible={banner.visible}
          isFloating
          position={{ top: '16px', right: '16px' }}
          onCancel={() => setBanner((prev) => ({ ...prev, visible: false }))}
          shouldAutoDismiss
          autoDismissDelay={5000}
          isCloseButtonVisible
          className={classes.alertBanner}
        />
      )}
      <div className={classes.content}>
        <div className={classes.splitLayout}>
          {/* Left: form panel */}
          <div className={classes.formPanel}>
            <div className={classes.mappedPanelHeader}>
              <div>
                <Typography className={classes.panelTitle}>Select Attributes</Typography>
                <Typography className={classes.panelDesc}>
                  Choose a Saviynt and source attribute to create a mapping.
                </Typography>
              </div>
            </div>
            <div className={classes.formPanelBody}>
              <div className={classes.dropdownContainer}>
                <Typography variant="subtitle2" className={classes.dropdownLabel}>
                  {ATTRIBUTE_MAPPING_LABELS.SAVIYNT_ATTRIBUTE}
                </Typography>
                <MenuInput
                  options={saviyntOpts}
                  onChange={(clicked) => {
                    setSaviyntOpts((prev) =>
                      prev.map((o) => ({
                        ...o,
                        isChecked: o.value === clicked?.value ? clicked.isChecked : false,
                      }))
                    );
                    setSaviyntAttribute(clicked?.isChecked ? clicked.value : '');
                  }}
                  placeholder={ATTRIBUTE_MAPPING_LABELS.SELECT_SAVIYNT}
                  backgroundColor="secondary"
                />
              </div>
              <div className={classes.dropdownContainer}>
                <Typography variant="subtitle2" className={classes.dropdownLabel}>
                  {sourceAttributeLabel}
                </Typography>
                <MenuInput
                  options={sourceOpts}
                  onChange={(clicked) => {
                    setSourceOpts((prev) =>
                      prev.map((o) => ({
                        ...o,
                        isChecked: o.value === clicked?.value ? clicked.isChecked : false,
                      }))
                    );
                    setSourceUserAttribute(clicked?.isChecked ? clicked.value : '');
                  }}
                  placeholder={sourceAttributePlaceholder}
                  backgroundColor="secondary"
                />
              </div>

              <div className={classes.addButtonRow}>
                <Button kind="filled" size="medium" onClick={handleAddMapping}>
                  {ATTRIBUTE_MAPPING_LABELS.ADD_MAPPING}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: mapped pairs panel */}
          <div className={classes.mappedPanel}>
            <div className={classes.mappedPanelHeader}>
              <Typography className={classes.panelTitle}>
                {ATTRIBUTE_MAPPING_LABELS.MAPPED_ATTRIBUTES}
                {mappedAttributes.length > 0 && (
                  <span className={classes.mappedCount}>{mappedAttributes.length}</span>
                )}
              </Typography>
              {selectedMappings.length > 0 && (
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  className={classes.deleteSelectedButton}
                >
                  <Icon kind="Delete" color="--color-critical-700" size="small" />
                  Delete ({selectedMappings.length})
                </button>
              )}
            </div>
            {mappedAttributes.length > 0 && (
              <div className={classes.mappingChipHeader}>
                <span className={classes.chipHeaderCheck} />
                <span className={classes.chipHeaderAttr}>Saviynt Attribute</span>
                <span className={classes.chipHeaderArrow} />
                <span className={classes.chipHeaderAttr}>{sourceAttributeLabel}</span>
              </div>
            )}
            <div className={classes.mappedPanelBody}>
              {mappedAttributes.length === 0 ? (
                <div className={classes.emptyState}>
                  <Icon kind="Mapping" size="medium" color="--color-neutral-300" />
                  <Typography className={classes.emptyStateText}>No mappings yet</Typography>
                </div>
              ) : (
                mappedAttributes.map((mapping) => (
                  <div
                    key={mapping.id}
                    className={`${classes.mappingChip} ${
                      selectedMappings.includes(mapping.id) ? classes.mappingChipSelected : ''
                    }`}
                  >
                    <Checkbox
                      isChecked={selectedMappings.includes(mapping.id)}
                      onChange={() => handleToggleSelect(mapping.id)}
                      size="small"
                    />
                    <span className={classes.chipAttr}>{mapping.saviyntProperty}</span>
                    <Icon kind="ArrowRight" size="small" color="--color-neutral-400" />
                    <span className={classes.chipAttr}>{mapping.sourceProperty}</span>
                  </div>
                ))
              )}
            </div>
            {mappedAttributes.length > 0 && (
              <div className={classes.mappedPanelFooter}>
                <button type="button" onClick={handleSelectAll} className={classes.selectAllBtn}>
                  {selectedMappings.length === mappedAttributes.length
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedMappings([])}
                  className={classes.clearAllBtn}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <Button kind="outlined" size="medium" onClick={handleSave} disabled={saveMappingLoading}>
          {saveMappingLoading
            ? ATTRIBUTE_MAPPING_LABELS.SAVING
            : ATTRIBUTE_MAPPING_LABELS.SAVE_MAPPINGS}
        </Button>
      </div>
    </>
  );
}

AttributeMappingContent.propTypes = {
  sourceID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sourceType: PropTypes.string,
  userAttributes: PropTypes.shape({
    saviyntUserAttributes: PropTypes.arrayOf(PropTypes.string),
    sourceUserAttributes: PropTypes.arrayOf(PropTypes.string),
    presentMapping: PropTypes.arrayOf(
      PropTypes.shape({
        saviyntProperty: PropTypes.string,
        sourceProperty: PropTypes.string,
      })
    ),
  }),
  userAttributesLoading: PropTypes.bool,
  saveMappingLoading: PropTypes.bool,
  saveMappingError: PropTypes.string,
  onSave: PropTypes.func,
};

AttributeMappingContent.defaultProps = {
  sourceID: null,
  sourceType: null,
  userAttributes: null,
  userAttributesLoading: false,
  saveMappingLoading: false,
  saveMappingError: null,
  onSave: null,
};

export default AttributeMappingContent;
